// Tabla horaria del bloque D de 018A (registro transanestésico), pura y testeable sin React.
//
// El papel imprime, bajo el gráfico, DOS cuadrículas por bloque horario:
//  - "AGENTE INHALATORIO / INFUSIÓN CONTINUA": 5 renglones (cada uno con su nombre a la izquierda);
//  - "PARÁMETROS DE MONITOREO ANESTÉSICO": 5 renglones fijos (Onda delta PP, Saturación O2, Capnometría,
//    Relajación neuromuscular, Profundidad anestésica).
// Antes había un solo renglón de agente y faltaba Saturación O2. El instructivo pide "registrar el nombre" del agente/infusión y los
// valores de los monitores "en números", así que cada renglón de agente lleva un nombre propio y un valor por columna horaria.
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';

export const FILAS_AGENTE = 5;
/** El papel dibuja 9 bloques horarios; se admiten hasta 10 columnas (una hora de holgura, como antes). */
export const TOPE_COLUMNAS_HORARIAS = 10;

export const PARAMETROS_MONITOREO = ['ondaDeltaPP', 'saturacionO2', 'capnometria', 'relajacionNeuromuscular', 'profundidadAnestesica'] as const;
export type ParametroMonitoreo = (typeof PARAMETROS_MONITOREO)[number];

export interface ColumnaHoraria {
  readonly id: string;
  readonly hora: string;
  /** Valor del renglón de agente i (0..4) en esta hora. */
  readonly agentes: readonly string[];
  readonly monitoreo: Readonly<Record<ParametroMonitoreo, string>>;
}

export interface EstadoTablaHoraria {
  /** Nombre del agente/infusión de cada uno de los 5 renglones. */
  readonly nombresAgentes: readonly string[];
  readonly columnas: readonly ColumnaHoraria[];
}

export function estadoInicialTablaHoraria(): EstadoTablaHoraria {
  return { nombresAgentes: Array.from({ length: FILAS_AGENTE }, () => ''), columnas: [] };
}

export function columnaHorariaVacia(): ColumnaHoraria {
  return {
    id: crypto.randomUUID(),
    hora: '',
    agentes: Array.from({ length: FILAS_AGENTE }, () => ''),
    monitoreo: { ondaDeltaPP: '', saturacionO2: '', capnometria: '', relajacionNeuromuscular: '', profundidadAnestesica: '' },
  };
}

function valoresPorHora(columnas: readonly ColumnaHoraria[], valorDe: (c: ColumnaHoraria) => string): string {
  return columnas
    .map((c) => (valorDe(c).trim() ? `${c.hora.trim() || '—'}: ${valorDe(c).trim()}` : ''))
    .filter(Boolean)
    .join(' | ');
}

/** `Par[]` de la tabla horaria: un par por renglón de agente (con su nombre) y uno por parámetro de monitoreo; los renglones sin nada se omiten. */
export function paresDeTablaHoraria(t: Traducir, estado: EstadoTablaHoraria): Par[] {
  const agentes: Par[] = Array.from({ length: FILAS_AGENTE }, (_, i) => {
    const nombre = (estado.nombresAgentes[i] ?? '').trim();
    const valores = valoresPorHora(estado.columnas, (c) => c.agentes[i] ?? '');
    return { pregunta: `${t('formularios.f018a.bloqueD.parametros.agenteInhalatorio')} ${i + 1}`, respuesta: [nombre, valores].filter(Boolean).join(' — ') };
  });
  const monitoreo: Par[] = PARAMETROS_MONITOREO.map((parametro) => ({
    pregunta: t(`formularios.f018a.bloqueD.parametros.${parametro}`),
    respuesta: valoresPorHora(estado.columnas, (c) => c.monitoreo[parametro]),
  }));
  return [...agentes, ...monitoreo].filter((p) => p.respuesta.trim() !== '');
}
